import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Circle, X } from 'lucide-react';

import { api } from '@/lib/api/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { useT } from '@/lib/i18n/useT';

/**
 * Caso 14 — Dashboard onboarding checklist. Visible until the workspace has
 * created and configured its first event. Each step links to the existing
 * page that owns the creation. Dismissed state lives in localStorage so a
 * user who skipped won't see it again on next sign-in.
 *
 * v1 derives "complete" by hitting the relevant list endpoints and asking
 * "are there any?" — no new backend needed. When Caso 16 (brand management)
 * lands, the brand step can re-check more precisely.
 */
export function FirstStepsChecklist() {
  const t = useT();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.localStorage.getItem('xotski.firstSteps.dismissed') === '1',
  );

  const brands = useQuery({
    queryKey: ['firstSteps', 'brands'],
    queryFn: () =>
      api
        .list<{ id: number }>('/co-brands', { page: 1, perPage: 1 })
        .catch(() => ({ items: [], total: 0 })),
    staleTime: 60_000,
  });
  const events = useQuery({
    queryKey: ['firstSteps', 'events'],
    queryFn: () =>
      api
        .list<{ id: number }>('/xk-event', { page: 1, perPage: 1 })
        .catch(() => ({ items: [], total: 0 })),
    staleTime: 60_000,
  });
  const stations = useQuery({
    queryKey: ['firstSteps', 'stations'],
    queryFn: () =>
      api
        .list<{ id: number }>('/xk-station', { page: 1, perPage: 1 })
        .catch(() => ({ items: [], total: 0 })),
    staleTime: 60_000,
  });

  const hasBrand = (brands.data?.total ?? 0) > 0;
  const hasEvent = (events.data?.total ?? 0) > 0;
  const hasStation = (stations.data?.total ?? 0) > 0;
  const allDone = hasBrand && hasEvent && hasStation;

  if (dismissed || allDone) return null;

  function dismiss() {
    window.localStorage.setItem('xotski.firstSteps.dismissed', '1');
    setDismissed(true);
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>{t('firstSteps.title')}</CardTitle>
        <button
          type="button"
          onClick={dismiss}
          aria-label={t('common.dismiss')}
          className="rounded-md p-1 hover:bg-[var(--color-bg-elevated)]"
        >
          <X className="h-4 w-4" />
        </button>
      </CardHeader>
      <CardContent>
        <ul className="space-y-2">
          <Step
            done={hasBrand}
            title={t('firstSteps.step.brand.title')}
            description={t('firstSteps.step.brand.description')}
            cta={t('firstSteps.step.brand.cta')}
            // v1 fix: was navigating to "/" — now lands on the actual brand
            // creation flow.
            onClick={() => navigate('/brand-studio/new')}
          />
          <Step
            done={hasEvent}
            title={t('firstSteps.step.event.title')}
            description={t('firstSteps.step.event.description')}
            cta={t('firstSteps.step.event.cta')}
            onClick={() => navigate(`/projects/${brands.data?.items?.[0]?.id ?? ''}`)}
            disabled={!hasBrand}
          />
          <Step
            done={hasStation}
            title={t('firstSteps.step.station.title')}
            description={t('firstSteps.step.station.description')}
            cta={t('firstSteps.step.station.cta')}
            onClick={() => navigate('/stations')}
            disabled={!hasEvent}
          />
        </ul>
      </CardContent>
    </Card>
  );
}

function Step(props: {
  done: boolean;
  title: string;
  description: string;
  cta: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <li className="flex items-start gap-3">
      <div className="mt-0.5">
        {props.done ? (
          <CheckCircle2 className="h-5 w-5 text-[var(--color-success,#34D399)]" />
        ) : (
          <Circle className="h-5 w-5 text-[var(--color-fg-subtle)]" />
        )}
      </div>
      <div className="flex-1">
        <div className="text-sm font-medium">{props.title}</div>
        <div className="text-xs text-[var(--color-fg-muted)]">
          {props.description}
        </div>
      </div>
      {!props.done && (
        <button
          type="button"
          onClick={props.onClick}
          disabled={props.disabled}
          className="rounded-md border border-[var(--color-border-default)] px-3 py-1 text-xs hover:border-[var(--color-accent)] disabled:cursor-not-allowed disabled:opacity-40"
        >
          {props.cta}
        </button>
      )}
    </li>
  );
}
